import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../App";
import { FamilyButton } from "../buttons/buttons";
import { IPDashboardContext } from "./Dashboard";

const IPChecklist = () => {
    const [checklist, setChecklist] = useState(null);
    const [activePhase, setActivePhase] = useState("");
    const [viewedPhase, setViewedPhase] = useState("");
    const [hasError, setHasError] = useState(null);
    const navigate = useNavigate();
    const { loggedInUser, request } = useContext(AppContext);
    const { setCurrentPage } = useContext(IPDashboardContext);

    useEffect(() => {
        setCurrentPage("checklist");
    });

    useEffect(() => {
        document.title = "Cofertility | Dashboard";

        const fetchChecklist = async () => {
            try {
                const response = await request({
                    url: `/checklist?program=family`,
                    method: "GET",
                });

                const { checklist } = response;
                setChecklist(checklist);

                outerloop: for (let i = 0; i < checklist.checklist_phases.length; i++) {
                    const phase = checklist.checklist_phases[i];
                    for (let j = 0; j < phase.checklist_tasks.length; j++) {
                        const task = phase.checklist_tasks[j];
                        for (let k = 0; k < task.subtasks.length; k++) {
                            if (task.subtasks[k].user_subtask.status === "active") {
                                setActivePhase(phase.name);
                                setViewedPhase(phase.name);
                                break outerloop;
                            }
                        }
                    }
                }
            } catch (error) {
                // TODO: handle this error
                console.error("Error fetching checklist:", error);
                setHasError(error);
            }
        };

        fetchChecklist();
    }, [request]);

    useEffect(() => {
        if (hasError) {
            throw hasError;
        }
    }, [hasError]);

    const toggleViewedPhase = (phaseName) => {
        if (viewedPhase === phaseName) {
            setViewedPhase("");
        } else {
            setViewedPhase(phaseName);
        }
    };

    const ctaNavigate = async (cta) => {
        if (cta.includes("calendly")) {
            window.open(`https://calendly.com/meetwithcofertility/family-by-co-intro-call?utm_content=${loggedInUser.id}`, "_blank");
        } else {
            navigate(cta);
        }
    };

    const determineDisplayedTask = (task) => {
        let data = {
            totalSubtasks: 0,
            reqSubtasks: 0,
            completeReq: 0,
            blockingTask: null,
            activeTask: null,
            inactiveTask: null,
            lastComplete: null,
        };

        let subtask = task.subtasks[0];
        let taskComplete = false;

        task.subtasks.forEach((sub) => {
            data.totalSubtasks++;
            if (sub.required) data.reqSubtasks++;
            if (sub.user_subtask.status === "blocker" && data.blockingTask === null) data.blockingTask = sub;
            if (sub.user_subtask.status === "active" && data.activeTask === null) data.activeTask = sub;
            if (sub.user_subtask.status === "finished") {
                data.completeReq++;
                data.lastComplete = sub;
            }
            if (sub.user_subtask.status === "inactive" && data.inactiveTask === null) data.inactiveTask = sub;
        });

        if (data.blockingTask !== null) {
            subtask = data.blockingTask;
        } else if (data.activeTask !== null) {
            subtask = data.activeTask;
        } else if (data.reqSubtasks !== 0 && data.reqSubtasks === data.completeReq) {
            taskComplete = true;
            subtask = data.lastComplete;
        } else if (data.blockingTask === null && data.lastComplete !== null) {
            taskComplete = true;
            subtask = data.lastComplete;
        } else {
            subtask = data.inactiveTask;
        }

        return (
            <div
                key={task.id}
                className={`rounded-lg bg-white border-2 border-black px-6 pt-8 pb-4 mb-4 ${subtask.user_subtask.status === "inactive" ? "opacity-50" : ""} ${taskComplete ? "bg-opacity-0" : ""}`}
            >
                <div className="md:flex flex-col gap-4">
                    <div>
                        <div className="flex">
                            {taskComplete ? (
                                <img className="max-w-none h-10" src={`${process.env.PUBLIC_URL}/images/task_check.svg`} alt="Complete your application" width="40" height="40" />
                            ) : (
                                <img className="max-w-none h-10" src={`${process.env.PUBLIC_URL}/images/${task.icon}.svg`} alt="Complete your application" width="40" height="40" />
                            )}
                            <div className="ml-3">
                                <h3 className="">{task.name}</h3>
                                <p className="mb-4">{subtask.description}</p>
                            </div>
                        </div>
                        <div className="md:flex items-center sm:ml-[50px]">
                            {!taskComplete ? (
                                subtask.badge_override ? (
                                    <div className="bg-tertiary-light border border-black rounded px-3 mb-2">
                                        <span className="uppercase text-xs leading-6">{subtask.badge_override}</span>
                                    </div>
                                ) : null
                            ) : null}
                        </div>
                    </div>
                    <div className="md:flex justify-end items-center w-full">
                        {!taskComplete ? (
                            subtask.cta_button ? (
                                <FamilyButton
                                    onClick={() => {
                                        if (subtask.user_subtask.status === "active" || subtask.user_subtask.status === "blocker") ctaNavigate(subtask.cta_button);
                                    }}
                                    label={subtask.cta_text}
                                    arrow={true}
                                    active={subtask.user_subtask.status === "active" || subtask.user_subtask.status === "blocker"}
                                />
                            ) : subtask.cta_text ? (
                                <div className="italic">{subtask.cta_text}</div>
                            ) : null
                        ) : null}
                    </div>
                </div>
                <span className="block md:hidden italic font-normal mt-6"></span>
            </div>
        );
    };

    return (
        <div id="inner-body" className="min-h-screen flex flex-col h-full w-full text-[#191919] bg-white-200 bg-cover bg-fixed bg-center sm:bg-top">
            <div id="app" className="pb-10">
                <div className="relative w-full z-1 bg-secondary mb-12 md:mb-22">
                    <header className="bg-secondary px-6 sm:px-10 z-0">
                        <div className="flex flex-col gap-1 max-w-container mx-auto mt-8 ">
                            <div className="bg-secondary">
                                <div className="max-w-container mx-auto flex flex-col gap-4">
                                    <div className="flex flex-col items-center">
                                        {/* <img src={`${process.env.PUBLIC_URL}/images/split_dashboard_icon.svg`} alt='Family dashboard'/> */}
                                        <h1 className="mb-7 mt-2 text-4xxl text-white">Welcome, {loggedInUser.firstName}!</h1>
                                        <div className="text-white">Have a question? Let's chat.</div>
                                        <div
                                            onClick={() => window.open(`https://calendly.com/meetwithcofertility/family-by-co-intro-call?utm_content=${loggedInUser.id}`, "_blank")}
                                            className="flex items-center text-white mb-5 font-semibold underline cursor-pointer"
                                        >
                                            <span>Schedule an intro call</span>
                                            <button>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-arrow-right" viewBox="0 0 16 16">
                                                    <path
                                                        fill-rule="evenodd"
                                                        d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8"
                                                    />
                                                </svg>
                                            </button>
                                        </div>
                                        <div className="flex flex-wrap md:flex-nowrap justify-center">
                                            {checklist &&
                                                checklist.checklist_phases.map((phase, index) => (
                                                    <span key={phase.id} className={`pizza-tracker-item-family ${activePhase === phase.name ? "active" : ""}`}>
                                                        <span className={`pizza-tracker-order-family ${activePhase === phase.name ? "active" : ""}`}>{index + 1}</span>
                                                        <span className="pizza-tracker-title">{phase.name}</span>
                                                        <span className="text-xs font-light">{phase.timeline}</span>
                                                    </span>
                                                ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="absolute left-0 w-full -z-1">
                            <svg viewBox="0 0 1440 49" fill="none" className="fill-secondary w-full" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2131 -301.5C2131 -107.924 1498.83 49.0004 719.004 49.0004C-60.8219 49.0004 -692.996 -107.924 -692.996 -301.5C-692.996 -318.767 -693 -351 -693 -351C-693 -351 -33.619 -351 719.004 -351C1446.7 -351 2131 -351 2131 -351C2131 -351 2131 -319.113 2131 -301.5Z"></path>
                            </svg>
                        </div>
                    </header>
                </div>

                <section className="mx-6 md:mx-0 md:mb-24">
                    <div className="max-w-content mx-auto flex flex-col gap-4">
                        <div className="flex flex-col gap-4 relative">
                            {checklist &&
                                checklist.checklist_phases.map((phase, index) => {
                                    return (
                                        <div key={phase.id} className="border-b mt-4 !border-black pb-8 pt-4">
                                            <h2 className="mb-0 px-6 -mx-6 flex items-center">
                                                <span className={`pizza-tracker-order-family basis-10 font-body font-normal mr-4 ${phase.name === activePhase ? "active" : "!bg-white"}`}>
                                                    {index + 1}
                                                </span>
                                                <div
                                                    onClick={() => toggleViewedPhase(phase.name)}
                                                    className="hover:text-secondary flex w-full items-center justify-between text-left !no-underline cursor-pointer flex-1 !font-normal"
                                                    aria-expanded={phase.name === viewedPhase}
                                                >
                                                    {phase.step_title}
                                                    <span className={`w-8 h-8 ml-4 transition-transform flex-shrink-0 ${phase.name === viewedPhase ? "-rotate-180" : ""}`}>
                                                        <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M10 13L16 19L22 13" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"></path>
                                                        </svg>
                                                    </span>
                                                </div>
                                            </h2>
                                            <div style={phase.name === viewedPhase ? { height: "auto" } : { height: "0px", overflow: "hidden", display: "none" }}>
                                                <div className="mt-7 !mb-0">
                                                    <div className="mb-8"></div>
                                                    <div>{phase.checklist_tasks.map((task) => determineDisplayedTask(task))}</div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default IPChecklist;
